import { apiClient } from '../utils/apiClient';

class TeamsService {
  async getTeams() {
    try {
      const response = await apiClient.get('/teams');
      // API returns { data: [...], meta: { unique_members: N } }
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch teams');
    }
  }

  async getTeam(teamId) {
    try {
      const response = await apiClient.get(`/teams/${teamId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch team');
    }
  }

  async createTeam(teamData) {
    try {
      const response = await apiClient.post('/teams', teamData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateTeam(teamId, teamData) {
    try {
      const response = await apiClient.put(`/teams/${teamId}`, teamData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update team');
    }
  }

  async deleteTeam(teamId) {
    try {
      const response = await apiClient.delete(`/teams/${teamId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete team');
    }
  }

  async joinTeam(inviteCode) {
    try {
      const response = await apiClient.post('/teams/join', { invite_code: inviteCode });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to join team');
    }
  }

  async leaveTeam(teamId) {
    try {
      const response = await apiClient.post(`/teams/${teamId}/leave`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to leave team');
    }
  }

  async regenerateInviteCode(teamId) {
    try {
      const response = await apiClient.post(`/teams/${teamId}/regenerate-invite`);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          'Failed to regenerate invite code'
      );
    }
  }

  // Members
  async getTeamMembers(teamId) {
    try {
      const response = await apiClient.get(`/teams/${teamId}/members`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch team members');
    }
  }

  async removeMember(teamId, memberId) {
    try {
      const response = await apiClient.delete(`/teams/${teamId}/members/${memberId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to remove member');
    }
  }

  async updateMemberRole(teamId, memberId, role) {
    try {
      const response = await apiClient.put(`/teams/${teamId}/members/${memberId}/role`, { role });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update member role');
    }
  }

  // Invitations
  async getTeamInvitations(teamId) {
    try {
      const response = await apiClient.get(`/teams/${teamId}/invitations`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch invitations');
    }
  }

  async resendInvitation(teamId, invitationId) {
    try {
      const response = await apiClient.post(`/teams/${teamId}/invitations/${invitationId}/resend`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to resend invitation');
    }
  }

  async cancelInvitation(teamId, invitationId) {
    try {
      const response = await apiClient.delete(`/teams/${teamId}/invitations/${invitationId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to cancel invitation');
    }
  }

  // Workspaces
  async getTeamWorkspaces(teamId) {
    try {
      const response = await apiClient.get(`/teams/${teamId}/workspaces`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch team workspaces');
    }
  }

  // Boards — authoritative list of boards shared with a team (Team page tab +
  // cross-device discovery before the team scope has synced locally).
  async getTeamBoards(teamId) {
    try {
      const response = await apiClient.get(`/teams/${teamId}/boards`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch team boards');
    }
  }

  // Record ownership when a board is created-as-team or shared into a team.
  // Board *content* rides the sync push; this endpoint owns membership/metadata.
  async shareBoardWithTeam(boardId, { team_id }) {
    try {
      const response = await apiClient.post(`/boards/${boardId}/share-with-team`, { team_id });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to share board with team');
    }
  }

  async removeTeamBoard(teamId, boardId) {
    try {
      const response = await apiClient.delete(`/teams/${teamId}/boards/${boardId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to remove team board');
    }
  }

  // Activity feed
  async getTeamActivity(teamId, type = null) {
    try {
      const params = type ? { type } : {};
      const response = await apiClient.get(`/teams/${teamId}/activity`, { params });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch team activity');
    }
  }

  // Logo upload
  async uploadTeamLogo(teamId, file) {
    try {
      const formData = new FormData();
      formData.append('logo', file);
      const response = await apiClient.post(`/teams/${teamId}/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to upload logo');
    }
  }

  /**
   * Team invitations addressed to the signed-in user.
   *
   * Teams had no way to ask this. Accepting needs the token, the token only
   * ever existed in an email, and that email's button pointed at a page the
   * marketing site does not serve — so an invitation was unreachable unless
   * somebody read the raw URL. Matched server-side on the VERIFIED address.
   */
  async getPendingInvitations() {
    try {
      const response = await apiClient.get('/team-invitations/pending');
      return response.data.invitations || [];
    } catch {
      // An empty list, not an error: "nothing pending" is the true answer for
      // almost everyone, and a banner that failed to load should be absent
      // rather than shouting.
      return [];
    }
  }

  async acceptInvitation(token) {
    const response = await apiClient.post('/team-invitations/accept', { token });
    return response.data;
  }

  async declineInvitation(token) {
    const response = await apiClient.post('/team-invitations/decline', { token });
    return response.data;
  }

  /**
   * The repository a team's projects, boards & documents sync with (card
   * 84628c27): `{ url, provider, set_by_id, set_at }` or null. Every member
   * reads it; owners/admins set and clear it. URL + provider only — the
   * server refuses a URL with a password and never stores a token.
   */
  async getTeamRepo(teamId) {
    const response = await apiClient.get(`/teams/${teamId}/git-repo`);
    return response.data?.git_repo || null;
  }

  async setTeamRepo(teamId, { url, provider }) {
    try {
      const response = await apiClient.put(`/teams/${teamId}/git-repo`, { url, provider });
      return response.data?.git_repo || null;
    } catch (error) {
      const data = error.response?.data;
      throw new Error(data?.errors?.url?.[0] ? `Repository URL ${data.errors.url[0].toLowerCase()}` : data?.error || 'Failed to save the team’s repository');
    }
  }

  async clearTeamRepo(teamId) {
    try {
      await apiClient.delete(`/teams/${teamId}/git-repo`);
      return null;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to remove the team’s repository');
    }
  }

  clearCache() {}
}

const teamsService = new TeamsService();
export default teamsService;
